import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { useTimesheet } from '../hooks/useTimesheet.js';
import { useTaskCodesForEmployee } from '../hooks/useTaskCodes.js';
import { WeekSelector } from '../components/WeekSelector.js';
import { TimelineView } from '../components/TimelineView.js';
import { EntryFormModal } from '../components/EntryFormModal.js';
import { WeeklyHoursTracker } from '../components/WeeklyHoursTracker.js';
import { TaskColorLegend } from '../components/TaskColorLegend.js';
import {
  ComplianceErrorDisplay,
  type ComplianceViolation,
} from '../components/ComplianceErrorDisplay.js';
import { submitTimesheet, exportTimesheetEntries, ApiRequestError } from '../api/client.js';
import { isCoveredWeek, submitHoursLine } from '../utils/weekly-target.js';
import type {
  CreateEntryRequest,
  UpdateEntryRequest,
  TimesheetEntryWithTaskCode,
  WeeklyTarget,
} from '@renewal/types';
import './Timesheet.css';

/**
 * Calculate age from date of birth
 */
function calculateAge(dateOfBirth: string, asOfDate: string): number {
  const dob = new Date(dateOfBirth + 'T00:00:00');
  const asOf = new Date(asOfDate + 'T00:00:00');

  let age = asOf.getFullYear() - dob.getFullYear();
  const monthDiff = asOf.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

/**
 * Submit refusals that are not compliance violations, worded for the person.
 * The server's message says what happened; this adds what to do next.
 */
const SUBMIT_REFUSALS: Record<string, { ruleName: string; remediation: string }> = {
  TIMESHEET_EMPTY: {
    ruleName: 'Nothing to submit',
    remediation: 'Add your hours for the week, then submit.',
  },
  TIMESHEET_SUBMIT_CONFLICT: {
    ruleName: 'The week changed',
    remediation: 'Check the hours on this page, then submit again.',
  },
  TIMESHEET_ALREADY_SUBMITTED: {
    ruleName: 'Already submitted',
    remediation: 'This week is no longer open. The page now shows its status.',
  },
};

/**
 * Check if a date is a default school day
 */
function _isDefaultSchoolDay(dateStr: string): boolean {
  const date = new Date(dateStr + 'T00:00:00');
  const dayOfWeek = date.getDay();

  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false;
  }

  const month = date.getMonth();
  const day = date.getDate();

  if (month > 7 || (month === 7 && day >= 28)) {
    return true;
  }
  if (month < 5 || (month === 5 && day <= 20)) {
    return true;
  }

  return false;
}

export function Timesheet() {
  const { weekStartDate } = useParams<{ weekStartDate?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    timesheet,
    totals,
    loading,
    error,
    saving,
    addEntry,
    addMultipleEntries,
    updateEntry,
    updateEntriesSchoolDay,
    deleteEntry,
    previewEntry,
    refresh,
    refreshQuietly,
    applyWeeklyTarget,
  } = useTimesheet({ weekStartDate });

  // Load task codes for the timeline view (wait until timesheet loaded to avoid double-fetch)
  const { taskCodes, loading: taskCodesLoading } = useTaskCodesForEmployee(
    timesheet ? user?.id : undefined,
    timesheet?.weekStartDate
  );

  const [editingEntry, setEditingEntry] = useState<TimesheetEntryWithTaskCode | null>(null);
  const [complianceErrors, setComplianceErrors] = useState<ComplianceViolation[]>([]);
  const [complianceErrorTitle, setComplianceErrorTitle] = useState<string | undefined>();
  // A refused delete or school-day change: shown above the grid, never in
  // place of it. Refused adds and edits show inside the popover or edit form.
  const [entryError, setEntryError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Messages about one week do not follow the person to another week.
  const timesheetId = timesheet?.id;
  useEffect(() => {
    setSubmitSuccess(false);
    setComplianceErrors([]);
    setEntryError(null);
  }, [timesheetId]);

  const handleWeekChange = (newWeekStartDate: string) => {
    if (newWeekStartDate === timesheet?.weekStartDate) return;
    navigate(`/timesheet/${newWeekStartDate}`);
  };

  // Handler for timeline view entries (directly adds without modal)
  const handleTimelineAddEntry = async (entry: {
    workDate: string;
    taskCodeId: string;
    startTime: string;
    endTime: string;
    isSchoolDay: boolean;
    supervisorPresentName?: string | null;
    mealBreakConfirmed?: boolean | null;
    notes?: string | null;
    fundId?: number | null;
  }) => {
    await addEntry(entry as CreateEntryRequest);
  };

  // Handler for bulk timeline entries (multi-day drag)
  const handleTimelineAddMultipleEntries = async (
    entries: Array<{
      workDate: string;
      taskCodeId: string;
      startTime: string;
      endTime: string;
      isSchoolDay: boolean;
      supervisorPresentName?: string | null;
      mealBreakConfirmed?: boolean | null;
      notes?: string | null;
      fundId?: number | null;
    }>
  ) => {
    await addMultipleEntries(entries as CreateEntryRequest[]);
  };

  const handleUpdateEntry = async (updates: CreateEntryRequest | UpdateEntryRequest) => {
    if (!editingEntry) return;
    await updateEntry(editingEntry.id, updates as UpdateEntryRequest);
    setEditingEntry(null);
  };

  const handleDeleteEntry = async (entryId: string) => {
    setEntryError(null);
    try {
      await deleteEntry(entryId);
    } catch (err) {
      setEntryError(err instanceof Error ? err.message : 'The entry could not be deleted.');
    }
  };

  const handleUpdateEntriesSchoolDay = async (date: string, isSchoolDay: boolean) => {
    setEntryError(null);
    try {
      await updateEntriesSchoolDay(date, isSchoolDay);
    } catch (err) {
      setEntryError(
        err instanceof Error ? err.message : 'The school day could not be changed for that day.'
      );
      throw err;
    }
  };

  const handleEditEntry = (entryId: string) => {
    if (!timesheet) return;
    const entry = timesheet.entries.find((e) => e.id === entryId);
    if (entry) {
      setEditingEntry(entry);
    }
  };

  const handleSubmit = async () => {
    if (!timesheet) return;

    setSubmitting(true);
    setComplianceErrors([]);
    setComplianceErrorTitle(undefined);
    setSubmitSuccess(false);

    try {
      const result = await submitTimesheet(timesheet.id);
      if (result.passed) {
        setSubmitSuccess(true);
        if (result.weeklyTarget) applyWeeklyTarget(result.weeklyTarget);
        // Refresh to get updated status (Submit stays busy until it lands)
        await refreshQuietly();
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        // A refusal carries the week as the server counted it at that moment.
        const weeklyTarget = err.responseBody['weeklyTarget'] as WeeklyTarget | undefined;
        if (weeklyTarget) applyWeeklyTarget(weeklyTarget);

        // Check for compliance violations in the response body
        const violations = err.responseBody['violations'] as ComplianceViolation[] | undefined;
        const refusal = err.code ? SUBMIT_REFUSALS[err.code] : undefined;
        if (violations && violations.length > 0) {
          setComplianceErrors(violations);
        } else if (refusal && err.code) {
          setComplianceErrorTitle('Not submitted');
          setComplianceErrors([
            {
              ruleId: err.code,
              ruleName: refusal.ruleName,
              message: err.message,
              remediation: refusal.remediation,
            },
          ]);
        } else {
          setComplianceErrors([
            {
              ruleId: 'ERROR',
              ruleName: 'Submission Error',
              message: err.message,
              remediation: 'Please try again or contact your supervisor.',
            },
          ]);
        }
      } else {
        setComplianceErrors([
          {
            ruleId: 'ERROR',
            ruleName: 'Submission Error',
            message: 'An unexpected error occurred.',
            remediation: 'Please try again or contact your supervisor.',
          },
        ]);
      }
      // Refresh to ensure state sync after failed submission
      await refreshQuietly();
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportEntries = async () => {
    if (!timesheet) return;
    try {
      const blob = await exportTimesheetEntries(timesheet.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet-entries-${timesheet.weekStartDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // Silently fail - the CSV isn't critical enough to show an error banner
    }
  };

  // "View affected entry": bring the block into view and give it focus, so a
  // keyboard user lands on it too (TimeBlock carries data-entry-id).
  const scrollToEntry = (entryId: string) => {
    const element = document.querySelector<HTMLElement>(`[data-entry-id="${entryId}"]`);
    if (element) {
      element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
    }
  };

  if (loading || taskCodesLoading) {
    return (
      <div className="timesheet-page">
        <div className="loading">Loading timesheet...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timesheet-page">
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={refresh} className="retry-button" data-testid="timesheet-retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!timesheet || !totals) {
    return (
      <div className="timesheet-page">
        <div className="error-message">
          <p>Timesheet not found</p>
        </div>
      </div>
    );
  }

  const employeeAge = user ? calculateAge(user.dateOfBirth, timesheet.weekStartDate) : 18;
  const isEditable = timesheet.status === 'open';

  // Weeks the 40-hour rule covers submit only when the server says the week is
  // ready (exactly 40 hours, nothing dated after today, no overlaps, quarter
  // hours). Earlier weeks keep the old rule: anything but an empty week.
  const coveredTarget = isCoveredWeek(totals.weeklyTarget) ? totals.weeklyTarget : null;
  const submitBlockers = coveredTarget?.submitBlockers ?? [];
  const submitDisabled =
    submitting ||
    timesheet.entries.length === 0 ||
    (coveredTarget !== null && !coveredTarget.ready);
  const submitDescribedBy = coveredTarget
    ? ['submit-hours-line', submitBlockers.length > 0 ? 'submit-blockers' : null]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return (
    <div className="timesheet-page">
      <header className="page-header">
        <h1 className="ri-display">Timesheets</h1>
        <WeekSelector selectedWeek={timesheet.weekStartDate} onWeekChange={handleWeekChange} />
        {saving && <span className="saving-indicator">Saving...</span>}
      </header>

      {timesheet.birthdayInWeek && (
        <div className="birthday-alert">
          <span className="birthday-icon">&#127874;</span>
          <div className="birthday-message">
            <strong>Birthday this week!</strong>
            <p>
              You turn {timesheet.birthdayInWeek.newAge} on{' '}
              {new Date(timesheet.birthdayInWeek.date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
              . Different hour limits may apply before and after your birthday.
            </p>
          </div>
        </div>
      )}

      {!isEditable && timesheet.status === 'submitted' && (
        <div className="status-alert status-alert--submitted" data-testid="status-submitted">
          <span className="status-icon">&#9889;</span>
          <div className="status-message">
            <strong>Submitted for Review</strong>
            <p>This timesheet is awaiting supervisor approval and cannot be edited.</p>
            {timesheet.submittedAt && (
              <p className="status-timestamp">
                Submitted on{' '}
                {new Date(timesheet.submittedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {!isEditable && timesheet.status === 'approved' && (
        <div className="status-alert status-alert--approved" data-testid="status-approved">
          <span className="status-icon">&#10003;</span>
          <div className="status-message">
            <strong>Approved</strong>
            <p>This timesheet has been approved by your supervisor.</p>
            {timesheet.reviewedAt && (
              <p className="status-timestamp">
                Approved on{' '}
                {new Date(timesheet.reviewedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
            {timesheet.supervisorNotes && (
              <p className="supervisor-notes">
                <strong>Supervisor notes:</strong> {timesheet.supervisorNotes}
              </p>
            )}
          </div>
        </div>
      )}

      {timesheet.status === 'open' && timesheet.supervisorNotes && timesheet.reviewedAt && (
        <div className="status-alert status-alert--rejected" data-testid="status-rejected">
          <span className="status-icon">&#9888;</span>
          <div className="status-message">
            <strong>Returned for Revision</strong>
            <p>
              Your supervisor has returned this timesheet with feedback. Please review the notes
              below, make any necessary corrections, and resubmit.
            </p>
            {timesheet.reviewedAt && (
              <p className="status-timestamp">
                Returned on{' '}
                {new Date(timesheet.reviewedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
            <div className="supervisor-feedback">
              <strong>Supervisor feedback:</strong>
              <p className="feedback-text">{timesheet.supervisorNotes}</p>
            </div>
            <p className="resubmit-hint">
              Make your corrections and click "Submit for Review" when ready.
            </p>
          </div>
        </div>
      )}

      {submitSuccess && (
        <div className="success-alert" data-testid="submit-success-alert">
          <span className="success-icon">&#10003;</span>
          <div className="success-message">
            <strong>Timesheet submitted.</strong>
            <p>Your timesheet has been sent to your supervisor for review.</p>
          </div>
        </div>
      )}

      {complianceErrors.length > 0 && (
        <ComplianceErrorDisplay
          violations={complianceErrors}
          title={complianceErrorTitle}
          onClose={() => setComplianceErrors([])}
          onEntryClick={scrollToEntry}
        />
      )}

      {entryError && (
        <div className="timesheet-entry-error" role="alert" data-testid="error-timesheet-entry">
          <span>{entryError}</span>
          <button
            type="button"
            className="timesheet-entry-error-dismiss"
            onClick={() => setEntryError(null)}
            aria-label="Dismiss"
            data-testid="timesheet-entry-error-dismiss-button"
          >
            &times;
          </button>
        </div>
      )}

      <div className="timesheet-content">
        <div className="timesheet-main">
          <TimelineView
            timesheet={timesheet}
            totals={totals}
            employeeAge={employeeAge}
            taskCodes={taskCodes}
            onAddEntry={handleTimelineAddEntry}
            onAddMultipleEntries={handleTimelineAddMultipleEntries}
            onPreviewEntry={previewEntry}
            onUpdateEntriesSchoolDay={handleUpdateEntriesSchoolDay}
            onEditEntry={handleEditEntry}
            onDeleteEntry={handleDeleteEntry}
            disabled={!isEditable}
          />
        </div>

        <aside className="timesheet-sidebar">
          <WeeklyHoursTracker totals={totals} isEditable={isEditable} />
          {timesheet.entries.length > 0 && <TaskColorLegend entries={timesheet.entries} />}
        </aside>
      </div>

      {isEditable && (
        <div className="timesheet-actions">
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            aria-describedby={submitDescribedBy}
            data-testid="submit-timesheet-button"
          >
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </button>
          {coveredTarget && (
            <div className="timesheet-submit-status" aria-live="polite">
              <p
                id="submit-hours-line"
                className={`timesheet-submit-line timesheet-submit-line--${
                  coveredTarget.ready ? 'ready' : coveredTarget.status
                }`}
                data-testid="submit-hours-line"
              >
                {submitHoursLine(coveredTarget)}
              </p>
              {submitBlockers.length > 0 && (
                <ul
                  id="submit-blockers"
                  className="timesheet-submit-blockers"
                  data-testid="submit-blockers"
                >
                  {submitBlockers.map((blocker, index) => (
                    <li
                      key={`${blocker.code}-${index}`}
                      data-testid={`submit-blocker-${blocker.code}`}
                    >
                      {blocker.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <p className="submit-hint">
            Once submitted, your timesheet will be reviewed by your supervisor.
          </p>
        </div>
      )}

      {timesheet.entries.length > 0 && (
        <div className="timesheet-export">
          <button
            className="export-button"
            onClick={handleExportEntries}
            disabled={submitting}
            data-testid="export-entries-button"
          >
            Export Entries CSV
          </button>
        </div>
      )}

      {/* Edit entry modal - used when clicking on existing time blocks */}
      {editingEntry && user && (
        <EntryFormModal
          isOpen={true}
          onClose={() => setEditingEntry(null)}
          onSubmit={handleUpdateEntry}
          entry={editingEntry}
          date={editingEntry.workDate}
          employeeId={user.id}
          employeeAge={calculateAge(user.dateOfBirth, editingEntry.workDate)}
          isSchoolDay={editingEntry.isSchoolDay}
        />
      )}
    </div>
  );
}
